var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { ArraySchema, MapSchema, Schema, type } from "@colyseus/schema";
import { Player } from "./Player.js";
import { Spectator } from "./Spectator.js";
import { CursorState } from "./CursorState.js";
import { NodeState } from "./NodeState.js";
import { TilePlacement } from "./TilePlacement.js";
import { CardPile } from "./CardPile.js";
import { HistoryEntry } from "./MoveHistory.js";
import { RestorePoint } from "./RestorePoint.js";
import { ChatMessage } from "./ChatMessage.js";
export class GameState extends Schema {
    constructor() {
        super(...arguments);
        this.phase = "lobby";
        /** Session id of the player whose turn it is. */
        this.currentPlayerId = "";
        /** Session id of the first player (drives Market Refresh). */
        this.firstPlayerId = "";
        this.roundNumber = 0;
        this.turnNumber = 0;
        /** Actions left in the current turn. */
        this.actionsRemaining = 0;
        /** Same-turn undo: what the current player's most recent Construct Building
         * / Construct Bridge action would take back ("Build Scout", "Build Bridge"),
         * or "" when there is nothing to undo. The board snapshot behind it lives
         * server-side; this label only drives the Undo button. It is armed by an
         * undoable action, cleared by any other action, and never survives the turn
         * ending — so an undo can never reach back past the player's own turn.
         * Deliberately NOT persisted: a resumed match starts with no undo point. */
        this.undoLabel = "";
        /** Buildings destroyed so far this round (combat + fortress demolitions) —
         * feeds round-end card effects (Giant Sphincter's ">3 in a round"). Reset at
         * every round start. */
        this.destroyedThisRound = 0;
        /** Fortresses implemented this game, by ANY player, cumulatively — a later
         * destruction never decrements it. Feeds Inverse Molehill's
         * `fortressDiscount.perImplementedFortress` ("X being the number of
         * fortresses implemented in this game"). CurdleCat's awarded copies don't
         * count (a copy is not an implement); BAM Exploder's extra artifact builds
         * do (each is a full implement). */
        this.fortressesImplemented = 0;
        /** Player who first took an action this round (first_action_token effects). */
        this.firstActionPlayerId = "";
        /** Player who most recently took an action this round
         * (round_end_last_actor_token effects — Penguin Puncher). */
        this.lastActionPlayerId = "";
        /** Identity keys (see `actionIdentityKey`) of the actions the CURRENT player
         * has spent an action point on this turn — the repeat detector behind Rinsed
         * Repeat Offender. Cleared at every turn start; persisted (and captured by
         * undo snapshots) so a rewind or a resumed match can't re-earn the token. */
        this.turnActionKeys = new ArraySchema();
        /** True once this turn's repeat has already paid out — the card's "max one
         * token per turn". Cleared with `turnActionKeys` at every turn start. */
        this.turnRepeatTokenTaken = false;
        /** True once the CURRENT player has taken any action this turn — one that
         * cost action points or one a card made free. A timed turn that runs out
         * without one is a Pass. Cleared at every turn start; persisted and
         * captured by undo snapshots like `turnActionKeys`. */
        this.turnActed = false;
        /** Targets whose defense has already counted this round (Vacmak'd). */
        this.defenseHeldNodeIds = new ArraySchema();
        /** Seats when the match went live. Win thresholds keyed on "number of
         * players" (Big Sprawler's 80/X) pin to this, so a mid-game quit can't move
         * a goal post. 0 on docs saved before the field existed → fall back to the
         * live seat count. */
        this.startingPlayerCount = 0;
        /** Session id of the winner once `phase === "ended"` ("" while live). Synced
         * (not just broadcast) so late spectators and reconnects see the result. */
        this.winnerId = "";
        /** The fortress card whose win condition ended the game ("" while live) —
         * drives the "won with <card>" readout on the game-over banner. */
        this.winCardId = "";
        /** Account id of the account that CREATED this match — the restore
         * authority, and unlike `hostUserId` it never moves and outlives the lobby:
         * it is re-seeded on every resume from the saved game document (or its
         * lobby record), so a match that survived a restart still knows whose
         * rollback command to accept. "" for guest-created matches, which have no
         * account to name. Synced so the client can gate the Turn History's restore
         * affordance without a round trip. */
        this.creatorUserId = "";
        // --- Lobby (only meaningful while phase === "lobby") --------------------
        /** Account id of the lobby host (creator, or promoted on host leave). */
        this.hostUserId = "";
        /** 6-char uppercase invite code. */
        this.inviteCode = "";
        /** "public" | "private". */
        this.visibility = "public";
        /** Seat cap for this lobby (also `maxClients`). */
        this.maxPlayers = 8;
        /** Wall-clock epoch ms when auto-start fires, or 0 when no countdown. */
        this.autoStartAtMs = 0;
        /** True when this match reports its result to a skill rating pool. Set by the
         * host in the lobby (with `poolId`); frozen once the match starts, and read
         * again at game over to decide whether to submit. */
        this.ranked = false;
        /** Inhouse pool id the result is rated in ("" when not ranked). */
        this.poolId = "";
        /** Display name of `poolId`, snapshotted so the lobby and the game-over
         * banner can name the pool without a lookup. */
        this.poolName = "";
        /** Session id of the round's first player while the round-start cycle window
         * is open — the round is paused before anything else happens until they cycle
         * out up to one card from each Fortress market (free) or keep both markets.
         * "" = no window open. No turn runs while it's open (`currentPlayerId` is
         * cleared), so every turn-gated command fails naturally. */
        this.roundStartCyclePlayerId = "";
        /** True while the end-of-turn defense window is open: the current player's
         * turn is ending, and each player in `pendingDefenders` may aim Barracks as
         * defenders (free, off-turn) before combat resolves and the turn passes on. */
        this.defenseWindow = false;
        /** True when the OPEN defense window was raised MID-TURN by a forced-attack
         * designation (Stab Pole Hole) rather than by the turn ending: closing it
         * resumes the designator's turn instead of resolving combat. */
        this.midTurnDefenseWindow = false;
        /** The winner-in-waiting while a Space Cheese Curdler last stand is in
         * progress ("" otherwise): their win was intercepted and executes when the
         * holder's grace turn concludes — unless the holder wins first. */
        this.pendingWinnerId = "";
        /** The card the pending winner met their win condition with ("" otherwise). */
        this.pendingWinCardId = "";
        /** Session id of the player currently taking a Space Cheese Curdler last
         * stand ("" otherwise). While set, win checks are scoped to this player. */
        this.graceTurnPlayerId = "";
        // --- Tutorial (only meaningful on a tutorial match) ----------------------
        /** True on a tutorial match: a scripted solo lesson against a bot rival
         * (`CreateOptions.tutorial`). Never persisted — a tutorial is a sandbox. */
        this.tutorial = false;
        /** Index into `TUTORIAL_STEPS` of the step the learner is on. Server-owned:
         * it advances when the step's goal is met on the board, or on a skip. */
        this.tutorialStep = 0;
        /** Nodes the current step points at (the glowing targets), if any. */
        this.tutorialNodeIds = new ArraySchema();
        /** The `"lo-hi"` edge key the current step points at (a bridge spot), or "". */
        this.tutorialEdge = "";
        /** Turns the last-stand holder still has in their solo round, the one in
         * progress included (0 outside a last stand). The "21"+ printing deals 3;
         * the "15"-"20" one deals a single grace turn. */
        this.graceTurnsRemaining = 0;
        /** Session ids still to respond to the open defense window (owners of attacked
         * buildings that would currently be destroyed). Emptied as each confirms. */
        this.pendingDefenders = new ArraySchema();
        /** Post-combat Barracks upkeep choices still owed, one entry per attacking
         * Barracks, encoded `"playerId|nodeId|foodCost"`. Non-empty = the turn is
         * frozen mid-end (like `defenseWindow`) until every owner keeps (pays the
         * Food) or lets the Barracks fall. Emptied as each resolves. */
        this.pendingUpkeep = new ArraySchema();
        /** Node ids whose Barracks already paid attack upkeep for its current aim.
         * An aim is charged once, at the first combat resolution it attacks in;
         * cleared when the aim clears (round end, untap, destruction). */
        this.upkeepPaidNodeIds = new ArraySchema();
        /** Post-combat resource-steal choices still owed, one entry per
         * attacker→victim pair, encoded `"attackerId|victimId|count"`. Non-empty =
         * the turn is frozen mid-end (like `pendingUpkeep`) until every attacker
         * picks which of the victim's resources to take. Only real choices land
         * here: an absent attacker, or a victim holding one resource type (or fewer
         * resources than the count), resolves immediately without an entry. */
        this.pendingSteals = new ArraySchema();
        this.players = new MapSchema();
        /** Connected watchers (keyed by session id). Watch-only: they hold no seat
         * and can send no effective command. Deliberately NOT persisted — spectating
         * is a property of a live connection, so a resumed match starts with none. */
        this.spectators = new MapSchema();
        /** Live pointer of every connected client (players AND spectators), keyed by
         * session id, so everyone can watch each other mouse around the board and
         * sidebars in realtime. Board coords are camera-independent (hex units from
         * the board centre — see `CursorState`). Connection-scoped presence like
         * `spectators`: deliberately NOT persisted, and an entry is deleted (not
         * flagged) whenever that pointer hides or its socket closes. */
        this.cursors = new MapSchema();
        /** Per-node state, indexed by node id (matches `buildBoard(...).nodes`). */
        this.nodes = new ArraySchema();
        /** The assembled board: which tile/face/rotation sits in each slot. */
        this.placements = new ArraySchema();
        /** Severed paths (chasms) as canonical `"lo-hi"` edge keys: the cut edges from
         * tile assembly, minus any a Bridge has reconnected. Derived from `placements`
         * (+ bridge nodes), but synced so the client renders them without recomputing.
         * Edges are derived from `BOARD_RADIUS`, so severing is an override set rather
         * than a mutation of the generated graph (CLAUDE.md). */
        this.severedEdges = new ArraySchema();
        /** Constructed Bridges as canonical `"lo-hi"` edge keys — the paths a player
         * has bridged. A bridge on a severed path reconnects it (removed from
         * `severedEdges` by `rebuildSevered`); a bridge may also span a connected path.
         * Synced so the client draws each bridge's I-beam on the path. */
        this.bridges = new ArraySchema();
        /** The Regular Fortress deck, split into hidden / face-up / discarded piles. */
        this.regularCards = new CardPile();
        /** The Win (victory) Fortress deck, split into hidden / face-up / discarded. */
        this.winCards = new CardPile();
        /** Chronological log of every action taken (oldest first), backing the
         * sidebar's Turn History view. Append-only and capped on the server; each
         * entry snapshots the actor's name/seat so it survives a player leaving. */
        this.moveHistory = new ArraySchema();
        /** The turn/round boundaries the creator may roll the match back to, oldest
         * first — see `RestorePoint`. Only the index is synced; the board snapshots
         * behind it are server-side. Deliberately NOT persisted with the game state
         * (the snapshot collection is the source of truth, and the room rebuilds
         * this list from it on resume). */
        this.restorePoints = new ArraySchema();
        /** Chronological chat log (oldest first), backing the bottom-left chat panel.
         * Append-only and capped on the server (last 1000 messages); each entry
         * snapshots the sender's name/seat so it survives them leaving. */
        this.chatMessages = new ArraySchema();
    }
}
__decorate([
    type("string")
], GameState.prototype, "phase", void 0);
__decorate([
    type("string")
], GameState.prototype, "currentPlayerId", void 0);
__decorate([
    type("string")
], GameState.prototype, "firstPlayerId", void 0);
__decorate([
    type("uint16")
], GameState.prototype, "roundNumber", void 0);
__decorate([
    type("uint16")
], GameState.prototype, "turnNumber", void 0);
__decorate([
    type("uint8")
], GameState.prototype, "actionsRemaining", void 0);
__decorate([
    type("string")
], GameState.prototype, "undoLabel", void 0);
__decorate([
    type("uint16")
], GameState.prototype, "destroyedThisRound", void 0);
__decorate([
    type("uint16")
], GameState.prototype, "fortressesImplemented", void 0);
__decorate([
    type("string")
], GameState.prototype, "firstActionPlayerId", void 0);
__decorate([
    type("string")
], GameState.prototype, "lastActionPlayerId", void 0);
__decorate([
    type(["string"])
], GameState.prototype, "turnActionKeys", void 0);
__decorate([
    type("boolean")
], GameState.prototype, "turnRepeatTokenTaken", void 0);
__decorate([
    type("boolean")
], GameState.prototype, "turnActed", void 0);
__decorate([
    type(["int16"])
], GameState.prototype, "defenseHeldNodeIds", void 0);
__decorate([
    type("uint8")
], GameState.prototype, "startingPlayerCount", void 0);
__decorate([
    type("string")
], GameState.prototype, "winnerId", void 0);
__decorate([
    type("string")
], GameState.prototype, "winCardId", void 0);
__decorate([
    type("string")
], GameState.prototype, "creatorUserId", void 0);
__decorate([
    type("string")
], GameState.prototype, "hostUserId", void 0);
__decorate([
    type("string")
], GameState.prototype, "inviteCode", void 0);
__decorate([
    type("string")
], GameState.prototype, "visibility", void 0);
__decorate([
    type("uint8")
], GameState.prototype, "maxPlayers", void 0);
__decorate([
    type("number")
], GameState.prototype, "autoStartAtMs", void 0);
__decorate([
    type("boolean")
], GameState.prototype, "ranked", void 0);
__decorate([
    type("string")
], GameState.prototype, "poolId", void 0);
__decorate([
    type("string")
], GameState.prototype, "poolName", void 0);
__decorate([
    type("string")
], GameState.prototype, "roundStartCyclePlayerId", void 0);
__decorate([
    type("boolean")
], GameState.prototype, "defenseWindow", void 0);
__decorate([
    type("boolean")
], GameState.prototype, "midTurnDefenseWindow", void 0);
__decorate([
    type("string")
], GameState.prototype, "pendingWinnerId", void 0);
__decorate([
    type("string")
], GameState.prototype, "pendingWinCardId", void 0);
__decorate([
    type("string")
], GameState.prototype, "graceTurnPlayerId", void 0);
__decorate([
    type("boolean")
], GameState.prototype, "tutorial", void 0);
__decorate([
    type("uint8")
], GameState.prototype, "tutorialStep", void 0);
__decorate([
    type(["int16"])
], GameState.prototype, "tutorialNodeIds", void 0);
__decorate([
    type("string")
], GameState.prototype, "tutorialEdge", void 0);
__decorate([
    type("uint8")
], GameState.prototype, "graceTurnsRemaining", void 0);
__decorate([
    type(["string"])
], GameState.prototype, "pendingDefenders", void 0);
__decorate([
    type(["string"])
], GameState.prototype, "pendingUpkeep", void 0);
__decorate([
    type(["int16"])
], GameState.prototype, "upkeepPaidNodeIds", void 0);
__decorate([
    type(["string"])
], GameState.prototype, "pendingSteals", void 0);
__decorate([
    type({ map: Player })
], GameState.prototype, "players", void 0);
__decorate([
    type({ map: Spectator })
], GameState.prototype, "spectators", void 0);
__decorate([
    type({ map: CursorState })
], GameState.prototype, "cursors", void 0);
__decorate([
    type([NodeState])
], GameState.prototype, "nodes", void 0);
__decorate([
    type([TilePlacement])
], GameState.prototype, "placements", void 0);
__decorate([
    type(["string"])
], GameState.prototype, "severedEdges", void 0);
__decorate([
    type(["string"])
], GameState.prototype, "bridges", void 0);
__decorate([
    type(CardPile)
], GameState.prototype, "regularCards", void 0);
__decorate([
    type(CardPile)
], GameState.prototype, "winCards", void 0);
__decorate([
    type([HistoryEntry])
], GameState.prototype, "moveHistory", void 0);
__decorate([
    type([RestorePoint])
], GameState.prototype, "restorePoints", void 0);
__decorate([
    type([ChatMessage])
], GameState.prototype, "chatMessages", void 0);
//# sourceMappingURL=GameState.js.map